import React from "react";
import { CaretUp, CaretDown } from "@phosphor-icons/react";

/**
 * Click-to-sort table header cell. Two-state toggle — every click flips the
 * direction so the result is always a deterministic sort the user can see:
 *   - ascFirst:  not sorted -> asc,  then asc <-> desc on each click
 *   - default:   not sorted -> desc, then desc <-> asc on each click
 * (v1.31.52 — removed the old third "clear" state: returning admins whose
 * saved sort was `desc` were clicking a column expecting A→Z and instead got
 * the list reset to its unsorted natural order, which read as "A→Z broken".)
 *
 * Usage:
 *   const [sort, setSort] = useState({ by: "uploaded_at", dir: "desc" });
 *   <SortHeader field="uploaded_at" sort={sort} onChange={setSort}>Uploaded</SortHeader>
 */
export default function SortHeader({ field, sort, onChange, children, align = "left", className = "", ascFirst = false }) {
    const active = sort?.by === field;
    const dir = active ? sort.dir : null;
    const cycle = () => {
        if (!active) {
            onChange({ by: field, dir: ascFirst ? "asc" : "desc" });
        } else {
            onChange({ by: field, dir: dir === "asc" ? "desc" : "asc" });
        }
    };
    const alignCls = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
    return (
        <button
            type="button"
            data-testid={`sort-${field}`}
            onClick={cycle}
            title={active ? (dir === "asc" ? "Sorted A → Z (low → high). Click for Z → A" : "Sorted Z → A (high → low). Click for A → Z") : (ascFirst ? "Click to sort A → Z" : "Click to sort Z → A")}
            className={`flex w-full items-center gap-1 select-none hover:underline underline-offset-4 ${alignCls} ${className}`}
        >
            <span>{children}</span>
            <span className="inline-flex h-3 w-3 flex-col items-center justify-center">
                <CaretUp size={9} weight="fill" className={`-mb-0.5 ${active && dir === "asc" ? "opacity-100" : "opacity-30"}`} />
                <CaretDown size={9} weight="fill" className={`${active && dir === "desc" ? "opacity-100" : "opacity-30"}`} />
            </span>
        </button>
    );
}

/**
 * Client-side generic sorter. Handles dates, numbers and strings correctly.
 *   sortRows(items, { by: "amount", dir: "desc" })
 * The `by` key can either be a field name OR a getter function `(row) => value`.
 */
export function sortRows(rows, sort) {
    if (!sort || !sort.by || !sort.dir) return rows;
    const getVal = typeof sort.by === "function" ? sort.by : (r) => r[sort.by];
    const factor = sort.dir === "asc" ? 1 : -1;
    // Detect type from first non-nullish value
    let sample;
    for (const r of rows) {
        const v = getVal(r);
        if (v !== null && v !== undefined && v !== "") { sample = v; break; }
    }
    const isNumberLike = typeof sample === "number" || (typeof sample === "string" && /^-?\d+(\.\d+)?$/.test(sample));
    const isDateLike = typeof sample === "string" && !isNumberLike && (
        /^\d{4}-\d{2}-\d{2}/.test(sample) ||        // ISO
        /^\d{1,2}[-/]\d{1,2}[-/]\d{4}/.test(sample) // DD-MM-YYYY or DD/MM/YYYY
    );

    const toKey = (v) => {
        if (v === null || v === undefined || v === "") return sort.dir === "asc" ? Infinity : -Infinity;
        if (isNumberLike) return Number(v);
        if (isDateLike) {
            // Normalise DD-MM-YYYY or DD/MM/YYYY to a Date
            if (typeof v === "string" && /^\d{1,2}[-/]\d{1,2}[-/]\d{4}/.test(v)) {
                const [d, m, y] = v.split(/[-/]/);
                return new Date(`${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`).getTime();
            }
            return new Date(v).getTime();
        }
        return String(v).toLowerCase();
    };

    return [...rows].sort((a, b) => {
        const av = toKey(getVal(a));
        const bv = toKey(getVal(b));
        if (av < bv) return -1 * factor;
        if (av > bv) return 1 * factor;
        return 0;
    });
}
